// The home widget FRAME's helpers (components/HomeWidgetFrame.js) — every
// value the frame shows that depends on more than its own state row, as
// registered functions so the component stays closure-free (frank) and both
// widget planes (the page's own widgets and module/app widgets) read the
// same answers.
//
// The frame's state row is the widget DESCRIPTOR:
//   { id, title, icon, kind: 'widget' | 'app', size, minSize, hue,
//     openRoute, openCall?, action?: { icon, label, call? | route? | set? },
//     help?, moduleId?, spec? (the manifest's legacy spec) }
// `openCall` names a registered function every open runs first (Broadcast
// marks its news seen).
// Module rows carry their manifest entry as-is, so every read below also
// looks in `spec` for the legacy spelling (iconBg, openRoute, w × h).
import { resolveColorRgb, getSafeInkInfo } from '@symbo.ls/scratch'
import { homeLayoutSizeOf } from './homeLayoutV2.js'
import { homeWidgetSizeForCell } from './homeGridMetrics.js'
import { homeWidgetSurfaceHue, posterFamilyEntry, homeWidgetHue } from './homeWidgetHue.js'
import { normalizeWidgetHelp } from './widgetHelp.js'
import { moduleStateScopeOf, scopeHostState, shellRootOf } from './moduleStateScope.js'

const _POSTER_FAMILY_KEYS = new Set([
  'studio',
  'tickets',
  'chat',
  'calendar',
  'reminders',
  'meet',
  'broadcast',
  'documents',
  'agents',
  'team',
  'analytics',
  'people',
  'health',
  'files'
])

// The accent ENTRY ({ bg, ink, … }, functions/homeWidgetHue.js) a widget
// paints its plate and its one visual with (HOME_WIDGETS_SPEC §3), or null
// for the neutral anchor. Precedence: the user's pick ("Add color") → the
// house map for a house id → the widget's declared family / hue → none.
export const homeWidgetAccent = function homeWidgetAccent(root, id, hue, designSystem) {
  const picked = homeWidgetSurfaceHue(root, id, designSystem)
  if (picked) return picked
  const house = homeWidgetHue(id, null, designSystem)
  if (house) return house
  if (typeof hue !== 'string' || !hue) return null
  if (_POSTER_FAMILY_KEYS.has(hue)) return posterFamilyEntry(hue)
  return homeWidgetHue(null, hue, designSystem)
}

const _css = (token, designSystem) => {
  if (!token) return null
  try {
    const r = resolveColorRgb(token, designSystem)
    if (!r || !Array.isArray(r.rgb)) return null
    const [red, green, blue] = r.rgb.map((n) => Math.round(Number(n) || 0))
    const a = r.alpha == null ? 1 : Number(r.alpha)
    return a >= 1 ? `rgb(${red}, ${green}, ${blue})` : `rgba(${red}, ${green}, ${blue}, ${a})`
  } catch (_) {
    return null
  }
}

// The WCAG non-text floor (a bar, a spark line, a dot is a graphic, not
// text), which is also the floor the ticket's acceptance names. `getSafeInk`
// accepts a candidate at `need - 0.02`, so a reading is compared with the
// same margin — otherwise a colour the design system just certified would be
// rejected here on the second decimal.
const _GRAPHICS_FLOOR = 3
const _FLOOR_MARGIN = 0.02

// The CARD's own surface token for one scheme — the ground every on-surface
// visual is measured against. The frame paints
// `var(--home-card-bg, var(--theme-card-background))`, i.e. the design
// system's `theme.card` half, and `--home-card-bg` is deliberately NOT read:
// that is a module / workspace-record re-tint written on `<html>` at runtime,
// and a ground an ancestor can repaint is not knowable when this runs (the
// same limit scratch's own `getSafeInk` header documents). In DARK the static
// ground is the LIGHTER of the two readings, so an accent that clears the bar
// against it clears more against a darker re-tint. No token is guessed when
// the design system cannot name its card: the caller falls back to the title
// ink, which reads on any surface the shell will paint.
const _cardGround = (designSystem, scheme) => {
  const card = designSystem && designSystem.theme && designSystem.theme.card
  const half = card && card['@' + scheme]
  const bg =
    (half && half.value && half.value.background) ||
    (card && card.value && card.value['@' + scheme] && card.value['@' + scheme].background)
  return typeof bg === 'string' && bg ? bg : null
}

// THE ACCENT a hue paints an ON-SURFACE visual with (a HomeWidgetList bar, a
// HomeWidgetProgress fill, a HomeWidgetSpark line, a HomeWidgetDelta chip, a
// HomeWidgetDots dot, the records block's stat and chart) — never the plate.
// The plate is a SURFACE, and it reads against the card only by accident:
// measured against the design system's own card halves, 6 of the 14 poster
// families fall under the 3:1 graphics floor in dark and 7 in light, with
// `analytics` worst at 1.19:1 — a near-black plate on a near-black card, the
// invisible Validators bars HOME-WIDGET-ACCENT-DARK-PLATE-INVISIBLE-1
// measured on dev.
//
// Derived by the design system's OWN rule, never decided here: scratch's
// `getSafeInk(hue, ground, { large: true, scheme })` holds the hue's OKLCH
// hue AND chroma and walks lightness until the rounded, composited colour
// clears the floor. So a family that already reads keeps its exact plate
// colour — and keeps it as `var(--color-poster<Family>)`, so a branded
// workspace's record override still re-tints the accent — while one that does
// not is brightened or deepened WITHIN its own family instead of swapped for
// a generic ink, which is what keeps a hue a hue. A designer who disagrees
// with a derivation authors `<hue>Safe` in `designSystem/color.js`, the
// escape hatch `getSafeInk` checks before it derives anything.
//
// Returns null when there is no accent inside the family: a translucent wash
// cannot reach the floor at ANY lightness, because the search preserves the
// alpha (the neutral `widgetPlate` tint tops out at 1.17:1 light / 1.51:1
// dark). The caller takes the title ink there.
const _accentFor = (bgToken, designSystem, scheme) => {
  const ground = _cardGround(designSystem, scheme)
  if (!bgToken || !ground || !designSystem) return null
  try {
    const info = getSafeInkInfo(bgToken, ground, { large: true, scheme }, designSystem)
    if (!info || !info.value || info.unresolved) return null
    if (info.ratio != null && info.ratio + _FLOOR_MARGIN < _GRAPHICS_FLOOR) return null
    return info.value
  } catch (_) {
    return null
  }
}

// BOTH halves of the accent, never the active one — `--home-widget-accent-light`
// and `--home-widget-accent-dark`, which the card's own `@dark` block collapses
// into `--home-widget-accent` (components/HomeWidgetFrame.js). The card's
// inline style is computed once at render and NOTHING re-renders it on a
// `data-theme` flip — the shell's theme watcher repaints the record's
// `--theme-*` vars on `<html>`, not a widget card — so an accent baked for
// whichever theme happened to be in force would be wrong the moment a member
// switches, and it is the DECISION that is theme-dependent here, not just the
// colour: `analytics` reads at 15.88:1 on the light card and 1.19:1 on the
// dark one. A `[data-theme="dark"] .<card>` rule follows the flip with no
// re-render, the same property the neutral `var(--color-…)` fallbacks have.
//
// The neutral anchor, and any hue with no accent inside its family, takes the
// card's own title ink — the rule the records block already documents ("the
// app hue, else the text ink", workspace 9571f3ede) — which is theme-adaptive
// on its own.
export const homeWidgetAccentOnSurfaceVars = function homeWidgetAccentOnSurfaceVars(
  bgToken,
  designSystem
) {
  return {
    '--home-widget-accent-light':
      _accentFor(bgToken, designSystem, 'light') || 'var(--color-title)',
    '--home-widget-accent-dark': _accentFor(bgToken, designSystem, 'dark') || 'var(--color-title)'
  }
}

// The CSS custom properties the frame publishes on its card. Two contracts,
// deliberately separate:
//   `--home-widget-hue` / `--home-widget-hue-ink` — the PLATE pair (the icon
//     squircle, the avatar faces): a surface and the ink that reads ON it.
//   `--home-widget-accent` — the ink an on-surface visual draws in, selected
//     per theme from the two halves above.
// A hue resolves to a concrete plate colour once — accent hues are
// theme-invariant (the poster families by design, raw hues by nature). The
// neutral anchor points at the adaptive tokens' own variables instead, so it
// follows the theme without a re-render.
export const homeWidgetAccentVars = function homeWidgetAccentVars(s, designSystem) {
  const hue = s && (s.hue || (s.spec && (s.spec.hue || s.spec.iconBg)) || s.iconBg)
  const entry = homeWidgetAccent(s && s.root, s && s.id, hue, designSystem)
  const bg = entry && _css(entry.bg, designSystem)
  const ink = entry && _css(entry.ink, designSystem)
  const plate =
    bg && ink
      ? { '--home-widget-hue': bg, '--home-widget-hue-ink': ink }
      : {
          '--home-widget-hue': 'var(--color-widgetPlate)',
          '--home-widget-hue-ink': 'var(--color-title)'
        }
  return {
    ...plate,
    ...homeWidgetAccentOnSurfaceVars(bg && ink ? entry.bg : null, designSystem)
  }
}

// An APP tile: a page-declared `app-*` shortcut, or a module widget its
// manifest declares icon-only — one cell, the launcher squircle and a name.
export const homeWidgetIsApp = function homeWidgetIsApp(s) {
  return !!s && (s.kind === 'app' || !!(s.spec && s.spec.iconOnly))
}

// The widget's own default size: the descriptor's `size`, else the size its
// legacy 12-column spec snaps to.
export const homeWidgetDefaultSize = function homeWidgetDefaultSize(s) {
  if (!s) return 's'
  if (homeWidgetIsApp(s)) return 's'
  const own = s.size || (s.spec && s.spec.size)
  if (own) return own
  const spec = s.spec || s
  return homeWidgetSizeForCell(spec.w, spec.h)
}

// The size the widget RENDERS at: an app tile is always one cell; a widget
// takes its saved size, else its default, never below its minimum.
export const homeWidgetFrameSize = function homeWidgetFrameSize(s) {
  if (!s || homeWidgetIsApp(s)) return 's'
  const min = s.minSize || (s.spec && s.spec.minSize)
  return homeLayoutSizeOf(s.root && s.root._homeLayout, s.id, homeWidgetDefaultSize(s), min)
}

// The size the card renders at before a member picks one: its default size,
// never below its minimum (homeWidgetFrameSize with no saved entry).
export const homeWidgetBaseSize = function homeWidgetBaseSize(s) {
  if (!s || homeWidgetIsApp(s)) return 's'
  const min = s.minSize || (s.spec && s.spec.minSize)
  return homeLayoutSizeOf(null, s.id, homeWidgetDefaultSize(s), min)
}

// The manifest's per-step span override (WS-HOME-GRID-SPAN-PER-STEP-1,
// functions/homeGridMetrics.js `homeWidgetSpanAt`): `spanAt` on the row, else
// in its legacy `spec` — the same two places `size` is read from. An app tile
// has none (always one cell).
export const homeWidgetSpanAtOf = function homeWidgetSpanAtOf(s) {
  if (!s || homeWidgetIsApp(s)) return null
  const own = s.spanAt || (s.spec && s.spec.spanAt)
  return own && typeof own === 'object' && !Array.isArray(own) ? own : null
}

// What the card spans at `size` and `cols`: the override only while `size` is
// the card's DEFAULT size (its declared size raised to its minimum — what it
// renders at before a member picks one); any other size spans its own cells.
// The card's own span variables (homeWidgetSpanVars) follow the same rule, so
// the resize outline and the card can never disagree. `this` = an element.
export const homeWidgetSpanFor = function homeWidgetSpanFor(s, size, cols) {
  const spanAt = homeWidgetSpanAtOf(s)
  if (!spanAt || size !== homeWidgetBaseSize(s)) return this.call('homeWidgetSpan', size, cols)
  return this.call('homeWidgetSpan', size, cols, spanAt)
}

// THE CARD'S OWN OVERRIDE (WS-HOME-GRID-SPAN-PER-STEP-1). A card publishes its
// manifest `spanAt` itself — the board sheet never lists widget ids — so the
// card that renders is always the row that decides (a module card that
// shadows a page widget carries the module's override, never the page's).
// Only at its default size: any other size spans its own cells.
// `--home-span-c-<cols>` / `--home-span-r-<cols>` for every step it names;
// the board's size rule reads them (functions/homeGridMetrics.js homeSpanVar).
// `this` = an element (el.call).
export const homeWidgetSpanVars = function homeWidgetSpanVars(s) {
  const spanAt = homeWidgetSpanAtOf(s)
  if (!spanAt || homeWidgetFrameSize(s) !== homeWidgetBaseSize(s)) return {}
  return this.call('homeSpanVarsFor', spanAt) || {}
}

// The card's `data-span-fit` (functions/homeGridMetrics.js homeSpanFitWords):
// the steps where its override changes the width its body really spans, or
// null.
export const homeWidgetSpanFit = function homeWidgetSpanFit(s) {
  const spanAt = homeWidgetSpanAtOf(s)
  if (!spanAt) return null
  const size = homeWidgetFrameSize(s)
  if (size !== homeWidgetBaseSize(s)) return null
  return this.call('homeSpanFitWords', size, spanAt) || null
}

// The frame's inline style: the accent variables and the span override
// variables. `this` = the frame.
export const homeWidgetFrameStyle = function homeWidgetFrameStyle(s, designSystem) {
  return { ...homeWidgetAccentVars(s, designSystem), ...homeWidgetSpanVars.call(this, s) }
}

// The widget's place in the flow (CSS `order`). A widget the layout does not
// list yet flows after every listed one, in declaration order.
export const homeWidgetFrameOrder = function homeWidgetFrameOrder(s) {
  const layout = s && s.root && s.root._homeLayout
  if (!Array.isArray(layout)) return 9999
  const at = layout.findIndex((e) => e && e.id === s.id)
  return at < 0 ? 9999 : at
}

// Hidden = the user's (or the workspace default's) hide, the same map the
// customize drawer restores from.
// The saved board steps UNDER the assistant's generated board (Page mode,
// ASSISTANT_BOARD_MODE_SPEC §4; DESIGN-WL-ASSISTANT-MAIN-HOME-1). While the view
// shows over the grid area, a card of the HOME board is inert and hidden from
// assistive tech — no focus, no click, no J / K stop reaches it — and fades
// out under the view; the board itself is untouched and returns with the same
// crossfade. A card of another board (`board: <id>`, the view's own) never
// steps under. The gate is the view's own rule (functions/assistantBoard.js
// `assistantBoardOpen`), read from the registry so a surface without the
// assistant (a test, a standalone page) simply reads false.
export const homeWidgetUnderView = function homeWidgetUnderView(s) {
  if (!s || (s.board && s.board !== 'home')) return false
  const root = s.root
  const fns = this && this.context && this.context.functions
  const open = fns && fns.assistantBoardOpen
  return !!(root && typeof open === 'function' && open.call(this, root))
}

export const homeWidgetFrameHidden = function homeWidgetFrameHidden(s) {
  const hidden = s && s.root && s.root._homeHiddenWidgets
  return !!(hidden && s && hidden[s.id] === true)
}

// The route the whole card opens.
export const homeWidgetOpenRoute = function homeWidgetOpenRoute(s) {
  return (s && (s.openRoute || (s.spec && s.spec.openRoute))) || null
}

// The ONE square button (HOME_WIDGETS_SPEC §2, decision H3 A): the widget's
// primary verb when it declares one, else ↗ Open when it has somewhere to go,
// else nothing. `{ icon, label }` — `label` is a bundle key; an `{app}` in it
// takes the widget's own title ("Open Received tasks": twenty ↗ buttons never
// share one accessible name).
export const homeWidgetActionOf = function homeWidgetActionOf(s) {
  const own = s && (s.action || (s.spec && s.spec.action))
  if (own && (own.call || own.route || own.set)) {
    return { icon: own.icon || 'plus', label: own.label || 'workspace.widget.action.add' }
  }
  if (homeWidgetOpenRoute(s)) return { icon: 'arrowUpRight', label: 'workspace.menu.openApp' }
  return null
}

// Open the widget's app — the whole card and the ↗ button. The window opens
// the one way every window opens (the PageSlot sheet's one motion recipe,
// DESIGN-WL-PAGESLOT-OPEN-SPLIT-SEARCH-1): the card no longer hands the slot
// a zoom origin — the iPad zoom-from-the-tile entrance is retired.
export const homeWidgetOpen = function homeWidgetOpen(s, event) {
  const route = homeWidgetOpenRoute(s)
  if (!route) return
  const before = s && (s.openCall || (s.spec && s.spec.openCall))
  if (before) {
    try {
      this.call(before, s.id)
    } catch (err) {
      console.warn('[home] widget open hook failed:', s && s.id, err && err.message)
    }
  }
  const opts = event && (event.metaKey || event.ctrlKey) ? { split: true } : undefined
  this.call('openAppPage', route, opts)
}

// Run the square button. A declared action runs its module function on the
// mounted BODY (so the module's own function registry resolves it), or opens
// its route; the default is Open.
//
// A "+" that declares `form` (pick 10 C — "a widget + opens only its form,
// never the whole page"; components/HomeWidgetFormHost.js has the contract)
// first asks the shell's form host to mount that module form, then runs the
// module's call with `{ form: true }` — the module seeds its draft (the sheet
// opens on it) instead of routing to its page. A module that does not read
// the flag keeps its old behaviour, so a form-declaring manifest is safe on
// any shell and a flag-reading module on any manifest.
export const homeWidgetRunAction = function homeWidgetRunAction(s, event, bodyEl) {
  const own = s && (s.action || (s.spec && s.spec.action))
  if (own && own.call) {
    const target = bodyEl || this
    const form = typeof own.form === 'string' && own.form ? own.form : null
    try {
      if (form) {
        this.call('openHomeWidgetForm', { form, moduleId: s.moduleId || null, widgetId: s.id })
        return target.call(own.call, s.id, event, { form: true })
      }
      return target.call(own.call, s.id, event)
    } catch (err) {
      console.warn('[home] widget action failed:', s && s.id, err && err.message)
      return undefined
    }
  }
  if (own && own.route) return this.call('openAppPage', own.route)
  // A declarative root patch — e.g. Your agents' ＋ opens the create-agent
  // modal by its root key.
  if (own && own.set && s.root) return s.root.update(own.set)
  return homeWidgetOpen.call(this, s, event)
}

// Ask the shell's form host (components/HomeWidgetFormHost.js) to mount a
// module's form. `seq` makes every press a fresh mount, so a form that was
// closed (its draft spent) opens clean on the next "+". Returns the request.
// el.call('openHomeWidgetForm', { form, moduleId, widgetId, open? })
//
// `open` (optional) = { call: '<fn>', args: [...] } — the call that SEEDS the
// form, run by the host ON the mounted form, so it resolves in the app's own
// function scope and writes the app's own state scope: the scope the form
// reads (an AI answer's form card, functions/assistantAppForms.js). It rides
// the request as a FROZEN copy, which the store keeps as a leaf — its args are
// data the host only hands on, never a nested store.
export const openHomeWidgetForm = function openHomeWidgetForm(req) {
  const root = this && this.state && this.state.root
  if (!root || typeof root.update !== 'function' || !req || !req.form) return null
  const prev = root._homeWidgetForm
  const next = {
    form: String(req.form),
    moduleId: req.moduleId || null,
    widgetId: req.widgetId || null,
    seq: ((prev && prev.seq) || 0) + 1
  }
  const open = req.open
  if (open && typeof open.call === 'string' && open.call) {
    next.open = Object.freeze({
      call: open.call,
      args: Object.freeze(Array.isArray(open.args) ? [...open.args] : [])
    })
  }
  root.update({ _homeWidgetForm: next })
  return next
}

// Mount the requested form INTO the host, with the module's own context — the
// SAME context a module widget body mounts with (pages/main.js
// _mountModuleWidgetBody): its components and functions resolve, its tokens
// paint, and it reads and writes the module's state scope, where the draft
// the module's call seeded lives. The app's shared globalScope bag rides a
// COPY of `scope`, never the bag itself. Idempotent per request (`seq`).
//
// THE SCOPE COMES FIRST. The host's own state is pointed at the form's state
// scope (the module's state root, or the shell root) BEFORE the mount, so the
// form's very first read — an `if:` on its own root runs before the plugin
// that scopes a module element at Init (moduleStateScope.js scopeHostState) —
// and every call made on it while parked already resolve in that scope. Then
// the request's `open` runs on the mounted form: the seed lands in the scope
// the form reads, on the first open, after a close, and for another record
// alike (WS-ASSISTANT-APP-FORM-OPEN-SHELL-SCOPE-1).
// el.call('mountHomeWidgetForm', hostEl, request)
export const mountHomeWidgetForm = function mountHomeWidgetForm(host, req) {
  if (!host || typeof host.set !== 'function' || !req || !req.form) return false
  const sig = `${req.moduleId || ''}/${req.form}/${req.seq || 0}`
  if (host.__homeFormSig === sig) return false
  // The shell root, whatever scope the host holds now.
  const root = shellRootOf((host.state && host.state.root) || null)
  const modCtx =
    req.moduleId && root && typeof root._moduleDsCtxGet === 'function'
      ? root._moduleDsCtxGet(req.moduleId)
      : null
  const def = { extends: req.form }
  if (modCtx) {
    def.context = modCtx
    if (modCtx.globalScope) def.scope = Object.assign({}, modCtx.globalScope)
  }
  scopeHostState(host, moduleStateScopeOf(modCtx))
  host.__homeFormSig = sig
  try {
    host.set(def)
  } catch (err) {
    host.__homeFormSig = null
    console.warn('[home] widget form mount failed:', req.form, err && err.message)
    return false
  }
  const open = req.open
  const form = host.content
  if (open && typeof open.call === 'string' && form && typeof form.call === 'function') {
    try {
      const ran = form.call(open.call, ...(Array.isArray(open.args) ? open.args : []))
      if (ran && typeof ran.then === 'function') {
        ran.then(null, (err) =>
          console.warn('[home] form open failed:', open.call, err && err.message)
        )
      }
    } catch (err) {
      console.warn('[home] form open failed:', open.call, err && err.message)
    }
  }
  return true
}

// THE PARAMS A BODY READS (FRAME CONTRACT v1, DESIGN-WL-ASSISTANT-MAIN-HOME-1):
// `el.scope.params` = the schema's defaults + this instance's values. Two row
// shapes reach the frame:
//   • a HOME row is the manifest widget entry itself — its `params` is the
//     SCHEMA (`{ <name>: { type, values?, default? } }`), and there are no
//     instance values (the home twin renders its defaults);
//   • a BOARD descriptor carries the manifest entry in `spec` (its `params`
//     is the schema) and this instance's values in `params`.
// A schema entry is an object with a `type`; a value is anything else. A
// body that ignores params renders its home twin.
const _isSchema = (p) =>
  !!p &&
  typeof p === 'object' &&
  !Array.isArray(p) &&
  Object.keys(p).length > 0 &&
  Object.values(p).every((v) => v && typeof v === 'object' && typeof v.type === 'string')
// A module widget's manifest entry → the home row (pages/main.js
// _allModuleWidgetsFor; the ModuleWidgets host mounts it with
// childrenAs: 'state'). `key` lets the children factory reconcile by widget id.
// The entry's PARAM SCHEMA ({ stage: { type: 'enum', values: [...] } }) is
// static manifest data that nothing writes through state; it goes in as a
// FROZEN copy, which the smbls store keeps as a leaf. Wrapped as a nested
// store, every enum's `values` key was a write of a reserved store-method
// name (7 "[smbls/state] … reserved store-method name" warnings per cold home
// load, dev d5b3389a4). The manifest entry itself is never frozen.
export const homeWidgetRow = function homeWidgetRow(w, moduleId) {
  const row = { ...w, key: w.id, moduleId }
  if (w.params && typeof w.params === 'object' && !Array.isArray(w.params)) {
    row.params = Object.freeze({ ...w.params })
  }
  return row
}

export const homeWidgetParams = function homeWidgetParams(s) {
  if (!s) return {}
  const spec = s.spec && typeof s.spec === 'object' ? s.spec : null
  const schema =
    (spec && _isSchema(spec.params) && spec.params) || (_isSchema(s.params) && s.params) || {}
  const own = s.params && typeof s.params === 'object' && !_isSchema(s.params) ? s.params : {}
  const out = {}
  for (const [name, def] of Object.entries(schema)) {
    if (def && def.default !== undefined) out[name] = def.default
  }
  for (const [name, value] of Object.entries(own)) {
    if (value !== undefined) out[name] = value
  }
  return out
}

// The words that name ONE item's hide — the edit-mode X of a widget card or of
// an app shortcut (its accessible name and its tooltip) and the context menu's
// "Hide" row: "Hide “{title}”" with the item's own title (the bundle's
// workspace.menu.hideWidget); an item with no title keeps the plain "Hide this
// widget". Every X used to read that same plain line — 40 widget X and 26 app
// X alike on a Georgian home (design-d2's probe, dev fecba977d) — so a
// screen-reader user could not tell which item an X hides.
export const homeWidgetHideLabel = function homeWidgetHideLabel(row) {
  const key = row && row.title
  if (!key) return this.call('polyglot', 'workspace.widget.action.hide')
  const title = this.call('polyglot', key)
  return String(this.call('polyglot', 'workspace.menu.hideWidget')).replace('{title}', title)
}

// The (?) beside the title: normalised `{ tip, ariaLabel }` or null.
export const homeWidgetHelpOf = function homeWidgetHelpOf(s) {
  return normalizeWidgetHelp(s && s.help)
}

// The size picker's rows in edit mode — S · M · L · XL, never below the
// widget's minimum (content sets the smallest size). The codes are size
// symbols, the same in every language (the track itself is named through
// the bundle: workspace.widget.size.label).
export const homeWidgetSizeRows = function homeWidgetSizeRows(s) {
  const all = ['s', 'm', 'l', 'xl']
  const min = (s && (s.minSize || (s.spec && s.spec.minSize))) || 's'
  const from = Math.max(0, all.indexOf(min))
  const label = { s: 'S', m: 'M', l: 'L', xl: 'XL' }
  return all.slice(from).map((size) => ({
    key: size,
    value: size,
    text: label[size],
    widgetId: s && s.id
  }))
}

// The size picker's `select` handler — `this` is the chosen segment, whose
// state row carries the widget id.
// THE SIZE ON A TOUCH SCREEN (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1). The
// picker above lives in edit mode, and on a phone edit mode opens the
// customize drawer over the whole board (measured on dev 586122071 at 393: the
// drawer 16..377 wide, 0 of 20 picker segments reachable until the drawer is
// tucked), while the avatar menu's "Customize dashboard" never showed under a
// /w/<org>/<workspace> address. So on a coarse pointer the widget's own menu
// (a long press — shared/functions/longPress.js) carries "Widget size" with the
// same rows: one row that opens S · M · L · XL, the current one checked, never
// below the minimum. A mouse keeps the menu it had. `this` = the card.
export const homeWidgetSizeMenuRow = function homeWidgetSizeMenuRow(s) {
  if (!s || !s.id || !this.call('isCoarsePointer')) return null
  const rows = homeWidgetSizeRows(s)
  if (rows.length < 2) return null
  const current = this.call('homeWidgetFrameSize', s)
  return {
    key: 'widgetSize',
    label: 'workspace.widget.size.label',
    children: rows.map((r) => ({
      key: `widgetSize-${r.value}`,
      action: 'widgetSize',
      label: r.text,
      checked: r.value === current,
      widgetId: s.id,
      size: r.value
    }))
  }
}

export const homeWidgetSetSize = function homeWidgetSetSize(size) {
  const row = this && this.state
  const root = row && row.root
  const id = row && row.widgetId
  if (!root || !id || !root._homeOps || typeof root._homeOps.setWidgetSize !== 'function') return
  root._homeOps.setWidgetSize(id, size)
}
